import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { DialogService } from '../../shared/dialog/dialog.service';
import { ChangesWarningPopup } from './changes-warning-popup/changes-warning-popup';
import { AbstractControl } from '@angular/forms';
import * as equal from 'fast-deep-equal';

export interface ControlChangesObject {
    control: AbstractControl;
    subscription: Subscription;
    initialValue: any;
}

@Injectable({
    providedIn: 'root',
})
export class ChangesService {
    private readonly controls = new Map<AbstractControl, ControlChangesObject>();

    constructor(private readonly dialogService: DialogService) {}

    /**
     * Connects control to check for changes when navigating away
     * @param control
     */
    connect(control: AbstractControl): void {
        if (this.controls.has(control)) return;

        const subscription = control.valueChanges.subscribe(() => {
            const prevData = this.controls.get(control);
            if (!prevData) throw new Error(`Data leak! Missing saved control in controls!`);

            this.controls.set(control, {
                control,
                initialValue: control.pristine ? control.value : prevData.initialValue,
                subscription: prevData.subscription,
            });
        });

        this.controls.set(control, {
            control,
            subscription,
            initialValue: control.value,
        });
    }

    /**
     * Disconnect control from detecting changes
     * @param control
     */
    disconnect(control: AbstractControl): void {
        const data = this.controls.get(control);
        if (!data) return;

        data.subscription.unsubscribe();
        this.controls.delete(control);
    }

    /**
     * Returns true if user interacted with control and made changes
     */
    hasChanges(): boolean {
        for (const { initialValue, control } of this.controls.values()) {
            if (control.dirty && !equal(control.value, initialValue)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Opens a warning popup and if users wants to discard changes emits true
     * otherwise false
     */
    warn(): Observable<boolean> {
        const clearIfConfirmed = (confirmed) => {
            if (confirmed) {
                this.controls.forEach((data) => {
                    this.controls.set(data.control, { ...data, initialValue: data.control.value });
                });
            }
        };
        return this.dialogService
            .spawnComponent(ChangesWarningPopup)
            .closed$.pipe(tap(clearIfConfirmed)) as Observable<boolean>;
    }
}
